import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { submitFeedback, addAnswerToKb, getSources, BASE_URL } from '../api/guru';
import SourcePicker from '../components/SourcePicker';
import ChartRenderer from '../components/ChartRenderer';
import AccountMenu from '../components/AccountMenu';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import guruLogo from '../assets/guru-logo.png';

const GREETING_TEMPLATES = [
  (name) => `${name} returns! 👋`,
  (name) => `Ready to rank, ${name}?`,
  (name) => `What are we optimising today, ${name}?`,
  (name) => `Let's get you ranking, ${name}!`,
  (name) => `Good to see you, ${name}!`,
  (name) => `Back for more insights, ${name}?`,
  (name) => `Hey ${name}, what's the SEO question today?`,
  (name) => `Let's dig into some data, ${name}!`,
  (name) => `What can Guru help with today, ${name}?`,
  (name) => `Time to outrank the competition, ${name}!`,
];

const SOURCE_LABELS = {
  knowledge_base: '📚 Knowledge Base',
  sheets:         '📋 Sheets',
  gsc:            '🔍 GSC',
  ga4:            '📊 GA4',
  karma:          '📈 Karma',
  gbp:            '📍 GMB',
  web_search:     '🌐 Web Search',
  llm_knowledge:  '🧠 SEO Best Practice',
  guard:          '🚫 Out of scope',
  error:          '⚠️ Error',
};

const SOURCE_CHIP_META = {
  gsc:    { emoji: '🔍', label: 'GSC' },
  ga4:    { emoji: '📊', label: 'GA4' },
  gsheet: { emoji: '📋', label: 'Sheets' },
  karma2: { emoji: '📈', label: 'Karma 2' },
  karma1: { emoji: '🔗', label: 'Karma 1' },
  gbp:    { emoji: '📍', label: 'GMB' },
  kb:     { emoji: '📚', label: 'Knowledge Base' },
  web:    { emoji: '🌐', label: 'Web' },
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Docx download button ──────────────────────────────────────────────────
// Detects __DOCX__:/api/v1/chat/export/filename.docx__ marker in content
// and renders a styled download button above the message text.
function DocxDownloadButton({ content }) {
  const match = content.match(/__DOCX__:(\/api\/v1\/chat\/export\/.+?\.docx)__/);
  if (!match) return null;
  const url = BASE_URL + match[1];
  const filename = url.split('/').pop();
  return (
    <a
      href={url}
      download={filename}
      className="docx-download-btn"
      target="_blank"
      rel="noreferrer"
    >
      <span className="docx-download-icon">📄</span>
      Download Word Report
    </a>
  );
}

// Strip the __DOCX__:...__  marker from the visible text
function stripDocxMarker(content) {
  return content.replace(/__DOCX__:\/api\/v1\/chat\/export\/.+?\.docx__\n*/g, '');
}

// ── Markdown message renderer ──────────────────────────────────────────────
function MessageContent({ content }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      className="message-md"
      components={{
        code({ inline, className, children }) {
          const match = /language-(\w+)/.exec(className || '');
          if (!inline && match && match[1] === 'chart') {
            try {
              const spec = JSON.parse(String(children).trim());
              return <ChartRenderer spec={spec} />;
            } catch (e) {
              return <pre className="chart-error">Chart parse error: {e.message}</pre>;
            }
          }
          return <code className={className}>{children}</code>;
        },
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

// ── Feedback widget ────────────────────────────────────────────────────────
// Phases:
//   'idle'       — shows 👍 👎
//   'asking-kb'  — "Add to Knowledge Base?" yes/skip buttons
//   'adding'     — spinner while calling addAnswerToKb
//   'added'      — "✅ Added to Knowledge Base" (permanent)
//   'noted'      — "👍 Noted" (thumbs up but user chose not to add to KB)
//   'correction' — correction textarea (after 👎)
//   'saving'     — saving correction
//   'noted-down' — "👎 Noted"
function FeedbackBar({ message, prevUserMsg, conversationId, msgIndex }) {
  const { setMessages } = useChat();

  const initPhase = () => {
    if (message.kbSourceId) return 'added';
    if (message.rating === 'down') return 'noted-down';
    return 'idle';
  };

  const [phase, setPhase]           = useState(initPhase);
  const [correction, setCorrection] = useState('');
  const [saving, setSaving]         = useState(false);
  const [toast, setToast]           = useState('');
  const checkedRef = useRef(false);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  // If message has a stored kbSourceId, verify the source still exists in KB.
  // If deleted, reset to idle so the user can click thumbs up again.
  useEffect(() => {
    if (!message.kbSourceId || checkedRef.current) return;
    checkedRef.current = true;
    getSources().then(({ data }) => {
      const ids = new Set((data.sources || []).map(s => s.id));
      if (!ids.has(message.kbSourceId)) {
        setPhase('idle');
        setMessages(prev => prev.map((m, i) =>
          i === msgIndex ? { ...m, kbSourceId: null, rating: null } : m
        ));
        // Also clear from sessionStorage
        try {
          if (conversationId) {
            const key = `guru-kb-${conversationId}`;
            const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
            delete stored[msgIndex];
            sessionStorage.setItem(key, JSON.stringify(stored));
          }
        } catch {}
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleThumbsUp = () => {
    if (phase !== 'idle') return;
    setPhase('asking-kb');
  };

  const handleThumbsDown = () => {
    if (phase !== 'idle') return;
    setPhase('correction');
  };

  const handleAddToKb = async () => {
    setPhase('adding');
    try {
      const label = (prevUserMsg || '').slice(0, 120) || 'Chat Answer';
      const { data } = await addAnswerToKb(message.content, label);
      const sourceId = data.source_id;
      // Persist source_id into the message state
      setMessages(prev => prev.map((m, i) =>
        i === msgIndex ? { ...m, kbSourceId: sourceId, rating: 'up' } : m
      ));
      // Persist to sessionStorage so it survives navigation within the session
      try {
        if (conversationId) {
          const key = `guru-kb-${conversationId}`;
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
          stored[msgIndex] = sourceId;
          sessionStorage.setItem(key, JSON.stringify(stored));
        }
      } catch {}
      setPhase('added');
    } catch {
      flash('Could not add to Knowledge Base.');
      setPhase('asking-kb');
    }
  };

  const handleSkipKb = () => {
    // User chose not to add to KB — go back to showing thumbs so they can
    // reconsider later. We don't record any feedback for a mere skip.
    setPhase('idle');
  };

  const saveCorrection = async () => {
    setSaving(true);
    try {
      await submitFeedback(conversationId, prevUserMsg, message.content, 'down', correction);
      setCorrection('');
      setPhase('noted-down');
    } catch {
      flash('Could not save feedback.');
    } finally { setSaving(false); }
  };

  const skipCorrection = async () => {
    setPhase('noted-down');
    try { await submitFeedback(conversationId, prevUserMsg, message.content, 'down'); } catch {}
  };

  return (
    <div className="feedback-area">
      {phase === 'idle' && (
        <div className="feedback-btns">
          <span className="feedback-label">Was this helpful?</span>
          <button className="fb-btn" onClick={handleThumbsUp} title="Good answer">👍</button>
          <button className="fb-btn" onClick={handleThumbsDown} title="Bad answer">👎</button>
        </div>
      )}

      {phase === 'asking-kb' && (
        <div className="feedback-btns">
          <button className="fb-btn fb-btn-confirm" onClick={handleAddToKb}>✅ Add to Knowledge Base</button>
          <button className="fb-btn fb-btn-skip" onClick={handleSkipKb}>Skip</button>
        </div>
      )}

      {phase === 'adding' && (
        <span className="feedback-done">Adding to Knowledge Base…</span>
      )}

      {phase === 'added' && (
        <span className="feedback-done">✅ Added to Knowledge Base</span>
      )}

      {(phase === 'noted-down') && !toast && (
        <span className="feedback-done">👎 Noted</span>
      )}

      {toast && <span className="feedback-toast">{toast}</span>}

      {(phase === 'correction' || phase === 'saving') && (
        <div className="correction-box">
          <p className="correction-hint">
            📝 Paste the correct answer below and Guru will learn from it:
          </p>
          <textarea
            className="correction-input"
            rows={4}
            placeholder="Paste the correct answer from a reliable source…"
            value={correction}
            onChange={(e) => setCorrection(e.target.value)}
          />
          <div className="correction-actions">
            <button
              className="btn btn-primary correction-save"
              onClick={saveCorrection}
              disabled={saving || !correction.trim()}
            >
              {saving ? 'Saving…' : '💾 Save to Knowledge Base'}
            </button>
            <button className="btn correction-skip" onClick={skipCorrection}>
              Skip
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Chat component ────────────────────────────────────────────────────
// Dev, 2026-07-03: the actual chat session — conversations, messages,
// loading/pendingQuestion, quota — now lives in ChatContext (see
// src/context/ChatContext.js), mounted once above the router in App.js, so
// it survives navigating away to /admin or /projects and back. This
// component only owns UI-local state that's fine to reset on remount (the
// "⋯" menu, the inline rename box, sort order).
export default function Chat() {
  const { user } = useAuth();
  const {
    conversations, activeId, messages, input, setInput, loading, initializing, statusSteps, pendingQuestion,
    quota, quotaExhausted, darkTheme, setDarkTheme,
    openConversation, newChat, deleteConv, renameConv,
    askQuestion, confirmSources, quickReply, cancelSourcePicker,
    broadcastDraft, confirmChatBroadcast, discardChatBroadcast, updateChatBroadcastBody, updateChatBroadcastSubject,
    meetDraft, confirmChatMeet, discardChatMeet,
  } = useChat();

  const [broadcastScheduleAt, setBroadcastScheduleAt] = useState('');
  const [broadcastScheduleMode, setBroadcastScheduleMode] = useState(false);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastEditMode, setBroadcastEditMode] = useState(false);
  // broadcastEditBody/Subject are ONLY used while in edit mode.
  // Preview ALWAYS reads from broadcastDraft directly — no fallback to these.
  const [broadcastEditBody, setBroadcastEditBody] = useState('');
  const [broadcastEditSubject, setBroadcastEditSubject] = useState('');

  // Enter edit mode: copy current committed draft into local edit state
  const handleEnterEditMode = () => {
    if (!broadcastDraft) return;
    setBroadcastEditBody(broadcastDraft.body_text || '');
    setBroadcastEditSubject(broadcastDraft.subject || '');
    setBroadcastEditMode(true);
  };

  // Save edits: commit local edit state back into broadcastDraft, return to preview
  const handleBroadcastSaveEdit = () => {
    updateChatBroadcastBody(broadcastEditBody);
    updateChatBroadcastSubject(broadcastEditSubject);
    setBroadcastEditMode(false);
  };

  const handleBroadcastSend = async () => {
    if (broadcastEditMode) return; // must save first
    setBroadcastSending(true);
    await confirmChatBroadcast(broadcastScheduleMode ? broadcastScheduleAt : null);
    setBroadcastSending(false);
    setBroadcastScheduleMode(false);
    setBroadcastScheduleAt('');
  };

  const greeting = useMemo(() => {
    const firstName = user?.name ? user.name.split(' ')[0] : null;
    if (!firstName) return 'Ask Guru!';
    const fn = GREETING_TEMPLATES[Math.floor(Math.random() * GREETING_TEMPLATES.length)];
    return fn(firstName);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // random pick on mount only
  const bottomRef = useRef(null);

  // Conversation rename / menu state — UI-local, fine to reset on remount.
  const [menuId, setMenuId]     = useState(null);
  const [menuPos, setMenuPos]   = useState({ top: 0, right: 0 });
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [sortAsc, setSortAsc]   = useState(false); // false = newest first (default)
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile/tablet off-canvas drawer (Dev, responsive pass)
  const cancelledRef = useRef(false);
  const menuRef = useRef(null);

  // Close the "⋯" menu on outside click
  useEffect(() => {
    if (!menuId) return;
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuId(null);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [menuId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, pendingQuestion]);

  const handleNewChat = () => { newChat(); };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    setMenuId(null);
    if (!window.confirm('Delete this conversation?')) return;
    await deleteConv(id);
  };

  const toggleMenu = (e, id) => {
    e.stopPropagation();
    if (menuId === id) { setMenuId(null); return; }
    // Calculate fixed position from the button so the menu escapes any
    // overflow:hidden/auto ancestors (sidebar, conv-list scroll container).
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPos({
      top:   rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
    setMenuId(id);
  };

  const startRename = (e, conv) => {
    e.stopPropagation();
    setMenuId(null);
    setEditingId(conv.id);
    setEditTitle(conv.title);
  };

  const commitRename = async (id) => {
    if (cancelledRef.current) { cancelledRef.current = false; setEditingId(null); return; }
    const title = editTitle.trim();
    setEditingId(null);
    if (!title) return;
    renameConv(id, title);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelledRef.current = true; e.currentTarget.blur(); }
  };

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    askQuestion(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const activeConv = conversations.find((c) => c.id === activeId);
  const visibleConversations = sortAsc ? [...conversations].reverse() : conversations;

  return (
    <div className="chat-page">

      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <div className={`gpt-sidebar${sidebarOpen ? ' gpt-sidebar-open' : ''}`}>
        <div className="gpt-sidebar-brand">
          <img src={guruLogo} alt="Guru" className="gpt-brand-logo" style={{width:32,height:32,borderRadius:6,objectFit:'contain'}} />
          <div>
            <div className="gpt-brand-name">Guru</div>
            <div className="gpt-brand-sub">SEO Knowledge Agent</div>
          </div>
        </div>

        <button className="gpt-new-chat-btn" onClick={handleNewChat}>
          <span className="gpt-new-chat-icon">✏️</span>
          New Chat
        </button>

        {(user?.is_admin || user?.is_project_manager) && (
          <div className="gpt-sidebar-topnav">
            {user?.is_admin && (
              <NavLink to="/admin" className="gpt-nav-link">
                <span>⚙️</span> Admin
              </NavLink>
            )}
            <NavLink to="/projects" className="gpt-nav-link">
              <span>📋</span> Projects
            </NavLink>
            {user?.is_admin && (
              <NavLink to="/notifications" className="gpt-nav-link">
                <span>📣</span> Notifications
              </NavLink>
            )}
            {user?.is_admin && (
              <NavLink to="/query-history" className="gpt-nav-link">
                <span>🔍</span> Query History
              </NavLink>
            )}
          </div>
        )}

        <div className="gpt-sidebar-spacer" />

        {conversations.length > 0 && (
          <div className="gpt-history-label">
            Recent
            <button
              className="gpt-history-sort-btn"
              onClick={() => setSortAsc((v) => !v)}
              title={sortAsc ? 'Showing oldest first' : 'Showing newest first'}
            >⇕</button>
          </div>
        )}

        <div className="gpt-conv-list">
          {conversations.length === 0 && (
            <p className="gpt-conv-empty">No conversations yet</p>
          )}
          {visibleConversations.map((conv) => (
            <div
              key={conv.id}
              className={`gpt-conv-item ${conv.id === activeId ? 'active' : ''}`}
              onClick={() => editingId !== conv.id && openConversation(conv.id)}
            >
              {editingId === conv.id ? (
                <input
                  className="gpt-conv-rename-input"
                  autoFocus
                  value={editTitle}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={handleRenameKeyDown}
                  onBlur={() => commitRename(conv.id)}
                />
              ) : (
                <div className="gpt-conv-title">{conv.title}</div>
              )}
              <div className="gpt-conv-row">
                <span className="gpt-conv-time">{timeAgo(conv.updated_at)}</span>
                <div
                  className="gpt-conv-actions"
                  ref={menuId === conv.id ? menuRef : null}
                >
                  <button
                    className="gpt-conv-menu-btn"
                    onClick={(e) => toggleMenu(e, conv.id)}
                    title="More options"
                  >⋮</button>
                  {menuId === conv.id && (
                    <div
                      className="gpt-conv-menu"
                      style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, bottom: 'auto' }}
                    >
                      <button
                        className="gpt-conv-menu-item"
                        onClick={(e) => startRename(e, conv)}
                      >✏️ Rename</button>
                      <button
                        className="gpt-conv-menu-item gpt-conv-menu-item-danger"
                        onClick={(e) => handleDelete(e, conv.id)}
                      >🗑 Delete</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <AccountMenu />
      </div>

      {/* ── Chat main ── */}
      <div className="chat-main">
        {/* ── Persistent header — always visible ── */}
        <div className="chat-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              className="mobile-menu-btn"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >☰</button>
            <div>
              <h1>{activeConv ? activeConv.title : 'Ask Guru'}</h1>
              <p>SEO knowledge agent — your data + the web</p>
              {quota && (
                <p className={`quota-status${quotaExhausted ? ' exhausted' : ''}`}>
                  {quota.is_admin
                    ? `💬 ${quota.used ?? '--'} queries made today`
                    : quotaExhausted
                      ? `🚫 Daily limit reached — resets at ${quota.resets_at}`
                      : `💬 ${quota.limit - quota.remaining} / ${quota.limit} queries used today`}
                </p>
              )}
            </div>
          </div>
          <button
            className="theme-toggle-btn"
            onClick={() => setDarkTheme(d => !d)}
            title={darkTheme ? 'Switch to light theme' : 'Switch to dark theme'}
          >
            {darkTheme ? '☀️' : '🌙'}
          </button>
        </div>

        {/* ── Landing view — shown when no messages yet and restore is complete ── */}
        {messages.length === 0 && !loading && !pendingQuestion && !initializing && (
          <div className="chat-landing">
            <div className="landing-body">
              <div className="landing-greeting">
                <img src={guruLogo} alt="Guru" className="landing-icon" />
                <h1>{greeting}</h1>
              </div>
              <div className="landing-input-wrap">
                <div className="landing-input-row">
                  <textarea
                    rows={4}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={quotaExhausted ? `Daily question limit reached — resets at ${quota.resets_at}` : 'Ask an SEO question…'}
                    disabled={quotaExhausted}
                    autoFocus
                  />
                  <button
                    className="send-btn"
                    onClick={handleSend}
                    disabled={!input.trim() || quotaExhausted}
                    title="Send"
                  >➤</button>
                </div>
              </div>
              <div className="landing-chips">
                <button className="landing-chip" onClick={() => setInput('What are the top ranking keywords for ')}>🔍 Rankings</button>
                <button className="landing-chip" onClick={() => setInput('Show me backlinks for ')}>🔗 Backlinks</button>
                <button className="landing-chip" onClick={() => setInput('What is the GSC performance this month for ')}>📊 GSC Data</button>
                <button className="landing-chip" onClick={() => setInput('Show me GA4 traffic trends for ')}>📈 Analytics</button>
                <button className="landing-chip" onClick={() => setInput('What does the knowledge base say about ')}>📚 Knowledge</button>
              </div>
            </div>
          </div>
        )}

        <div className={messages.length === 0 && !loading && !pendingQuestion && !initializing ? 'chat-messages-hidden' : 'chat-messages'}>
          {messages.map((msg, i) => {
            const prevUser = messages.slice(0, i).reverse().find(m => m.role === 'user') || null;

            // ── Meet draft card ───────────────────────────────────────────
            if (msg.role === 'meet_draft' || msg.role === 'meet_sent') {
              const fmtDt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
              return (
                <div key={msg.id || i} className="message assistant">
                  <div className="message-avatar">
                    <img src={guruLogo} alt="Guru" className="avatar-logo" />
                  </div>
                  <div className="message-content">
                    {msg.role === 'meet_sent' ? (
                      <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '14px 18px', color: '#065f46', fontSize: '14px' }}>
                        ✅ Google Meet created and invite email sent!
                      </div>
                    ) : meetDraft?.drafting ? (
                      <div style={{ color: '#6b7280', fontSize: '14px', padding: '8px 0' }}>
                        📅 Creating Google Meet…
                      </div>
                    ) : meetDraft?.error ? (
                      <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '10px', padding: '14px', fontSize: '13px', color: '#991b1b' }}>
                        ❌ {meetDraft.error}
                      </div>
                    ) : meetDraft ? (
                      <div style={{ background: '#eff6ff', border: '2px solid #3b82f6', borderRadius: '12px', padding: '18px', maxWidth: '560px' }}>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e40af', marginBottom: '14px' }}>
                          📅 Google Meet ready — send invite?
                        </div>
                        {/* Meeting details */}
                        <div style={{ background: '#fff', borderRadius: '8px', padding: '14px', marginBottom: '14px', border: '1px solid #bfdbfe' }}>
                          <div style={{ fontSize: '15px', fontWeight: 700, marginBottom: '10px', color: '#1e3a8a' }}>
                            {meetDraft.title}
                          </div>
                          <div style={{ display: 'grid', gap: '6px', fontSize: '13px', color: '#374151' }}>
                            <div>🕐 <strong>Start:</strong> {fmtDt(meetDraft.start)}</div>
                            <div>🕑 <strong>End:</strong>   {fmtDt(meetDraft.end)}</div>
                            <div>👥 <strong>To:</strong> {
                              meetDraft.attendee_names?.length
                                ? meetDraft.attendee_names.join(', ')
                                : meetDraft.attendee_emails?.join(', ')
                            }</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {meetDraft.attendee_emails?.join(', ')}
                            </div>
                          </div>
                          <div style={{ marginTop: '12px', padding: '10px 14px', background: '#eff6ff', borderRadius: '7px', border: '1px solid #bfdbfe' }}>
                            <a
                              href={meetDraft.meet_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#1d4ed8', fontWeight: 600, fontSize: '13px', textDecoration: 'none' }}
                            >
                              🎥 {meetDraft.meet_link}
                            </a>
                          </div>
                        </div>
                        {/* Email preview */}
                        <details style={{ marginBottom: '14px' }}>
                          <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#6b7280', fontWeight: 600 }}>
                            📧 Preview invite email
                          </summary>
                          <div style={{ marginTop: '8px', background: '#fff', borderRadius: '7px', padding: '12px', border: '1px solid #e5e7eb', fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                            <div style={{ marginBottom: '6px', color: '#6b7280' }}>
                              <strong>Subject:</strong> {meetDraft.email_subject}
                            </div>
                            <hr style={{ border: 'none', borderTop: '1px solid #f3f4f6', margin: '8px 0' }} />
                            {meetDraft.email_body}
                          </div>
                        </details>
                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={confirmChatMeet}
                            style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            ✅ Send Invite Email
                          </button>
                          <button
                            onClick={discardChatMeet}
                            style={{ background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', padding: '9px 14px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}
                          >
                            ✕ Discard
                          </button>
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '11px', color: '#9ca3af' }}>
                          ℹ️ Calendar invite already sent to attendees. This button sends the email notification.
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            }
            // ── End meet card ──────────────────────────────────────────────

            // ── Broadcast draft card ──────────────────────────────────────
            if (msg.role === 'broadcast_draft' || msg.role === 'broadcast_sent') {
              const isSent = msg.role === 'broadcast_sent';
              return (
                <div key={msg.id || i} className="message assistant">
                  <div className="message-avatar">
                    <img src={guruLogo} alt="Guru" className="avatar-logo" />
                  </div>
                  <div className="message-content">
                    {isSent ? (
                      <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '10px', padding: '14px 18px', color: '#065f46', fontSize: '14px' }}>
                        ✅ {msg.scheduledAt ? `Email scheduled for ${new Date(msg.scheduledAt).toLocaleString()}` : 'Email sent successfully!'}
                      </div>
                    ) : broadcastDraft?.drafting ? (
                      <div style={{ color: '#6b7280', fontSize: '14px', padding: '8px 0' }}>✨ Drafting email…</div>
                    ) : broadcastDraft?.error ? (
                      <div style={{ color: '#dc2626', fontSize: '14px' }}>{broadcastDraft.error}</div>
                    ) : broadcastDraft ? (
                      <div style={{ background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: '12px', padding: '18px', boxSizing: 'border-box', transition: 'width 0.2s', width: broadcastEditMode ? '680px' : '600px', minWidth: broadcastEditMode ? '680px' : '560px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '10px', color: '#92400e' }}>
                          📧 Email Draft — is this what you want to send?
                        </div>
                        <div style={{ background: '#fff', border: '1px solid #fde68a', borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
                          {/* To */}
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                            <strong>To:</strong>{' '}
                            {broadcastDraft.recipient_mode === 'all'
                              ? 'All Employees'
                              : broadcastDraft.recipient_names?.length
                                ? broadcastDraft.recipient_names.join(', ')
                                : (broadcastDraft.recipient_emails || []).join(', ') || 'Selected recipients'}
                          </div>
                          {/* Emails */}
                          {broadcastDraft.recipient_mode !== 'all' && broadcastDraft.recipient_emails?.length > 0 && (
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>
                              {broadcastDraft.recipient_emails.join(', ')}
                            </div>
                          )}
                          {/* Subject */}
                          <div style={{ marginBottom: '10px' }}>
                            <strong style={{ fontSize: '12px', color: '#6b7280' }}>Subject:</strong>{' '}
                            {broadcastEditMode ? (
                              <input
                                value={broadcastEditSubject}
                                onChange={e => setBroadcastEditSubject(e.target.value)}
                                style={{ display: 'block', fontSize: '13px', padding: '4px 8px', borderRadius: '5px', border: '1px solid #d1d5db', width: '100%', marginTop: '4px', boxSizing: 'border-box' }}
                              />
                            ) : (
                              <span style={{ fontSize: '13px', color: '#111827' }}>{broadcastDraft.subject}</span>
                            )}
                          </div>
                          <hr style={{ border: 'none', borderTop: '1px solid #fde68a', margin: '8px 0' }} />
                          {/* Body */}
                          {broadcastEditMode ? (
                            <textarea
                              value={broadcastEditBody}
                              onChange={e => setBroadcastEditBody(e.target.value)}
                              rows={14}
                              style={{ display: 'block', width: '100%', minWidth: 0, fontSize: '13px', lineHeight: 1.6, padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginTop: '4px' }}
                            />
                          ) : (
                            // Render as paragraphs — avoids huge gaps from \n\n in <pre>
                            <div style={{ fontSize: '13px', lineHeight: 1.6, color: '#111827' }}>
                              {(broadcastDraft.body_text || '').split(/\n\n+/).map((para, pi) => (
                                <p key={pi} style={{ margin: '0 0 8px 0' }}>
                                  {para.split('\n').map((line, li, arr) => (
                                    <span key={li}>{line}{li < arr.length - 1 && <br />}</span>
                                  ))}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                        {broadcastScheduleMode && (
                          <div style={{ marginBottom: '10px' }}>
                            <input
                              type="datetime-local"
                              value={broadcastScheduleAt}
                              onChange={e => setBroadcastScheduleAt(e.target.value)}
                              style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}
                            />
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          {broadcastEditMode ? (
                            // ── Edit mode: Save + Cancel ──
                            <>
                              <button
                                onClick={handleBroadcastSaveEdit}
                                style={{ background: '#16a34a', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                              >
                                💾 Save &amp; Preview
                              </button>
                              <button
                                onClick={() => setBroadcastEditMode(false)}
                                style={{ background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', padding: '8px 14px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}
                              >
                                ✕ Cancel
                              </button>
                            </>
                          ) : (
                            // ── Preview mode: Send / Schedule / Edit / Discard ──
                            <>
                              <button
                                onClick={handleBroadcastSend}
                                disabled={broadcastSending || (broadcastScheduleMode && !broadcastScheduleAt)}
                                style={{ background: '#4f46e5', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '7px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                              >
                                {broadcastSending ? 'Sending…' : broadcastScheduleMode ? '🕐 Schedule' : '✅ Send Now'}
                              </button>
                              <button
                                onClick={() => setBroadcastScheduleMode(m => !m)}
                                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', padding: '8px 14px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}
                              >
                                {broadcastScheduleMode ? '✕ Cancel Schedule' : '🕐 Schedule'}
                              </button>
                              <button
                                onClick={handleEnterEditMode}
                                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', padding: '8px 14px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={discardChatBroadcast}
                                style={{ background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', padding: '8px 14px', borderRadius: '7px', fontSize: '13px', cursor: 'pointer' }}
                              >
                                ✕ Discard
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            }
            // ── End broadcast card ─────────────────────────────────────────

            return (
              <div key={msg.id || i}>
                <div className={`message ${msg.role}`}>
                  <div className="message-avatar">
                    {msg.role === 'user'
                      ? (user?.picture
                          ? <img src={user.picture} alt={user?.name || 'You'} className="gpt-account-avatar-img" referrerPolicy="no-referrer" />
                          : '👤')
                      : <img src={guruLogo} alt="Guru" className="avatar-logo" />}
                  </div>
                  <div className="message-content">
                    {msg.role === 'assistant' && msg.timelineSteps && msg.timelineSteps.length > 0 && (
                      <div className="msg-timeline">
                        {msg.timelineSteps.map((step, si) => (
                          <div key={si} className="timeline-step done">
                            <div className="timeline-dot" />
                            <span className="timeline-label">{step.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {msg.role === 'assistant' &&
                     msg.timelineSteps?.some(s => s.label?.includes('Content Tracker')) && (
                      <div className="ct-data-notice">
                        <span className="ct-data-notice-icon">ℹ️</span>
                        <span>
                          Content Tracker has data from <strong>May 2026</strong> onwards.
                          Data prior to May 2026 is not accessible.
                        </span>
                      </div>
                    )}
                    <div className="message-bubble">
                      {msg.role === 'assistant' ? (
                        <>
                          <DocxDownloadButton content={msg.content} />
                          <MessageContent content={stripDocxMarker(msg.content)} />
                        </>
                      ) : msg.content}
                    </div>
                    {msg.role === 'assistant' && msg.quickReplies && msg.quickReplies.length > 0 && (
                      <div className="quick-reply-box">
                        <div className="quick-reply-box-title">Pick one to continue</div>
                        <div className="quick-reply-chips">
                          {msg.quickReplies.map((option, idx) => {
                            const isUsed = msg.quickReplyUsed != null;
                            const isSelected = msg.quickReplyUsed === option;
                            return (
                              <button
                                key={idx}
                                type="button"
                                className={`quick-reply-chip ${isSelected ? 'selected' : ''}`}
                                disabled={isUsed || loading || quotaExhausted}
                                onClick={() => quickReply(option, msg.selectedSources || [], msg.id)}
                              >
                                {option}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {msg.role === 'assistant' && (
                      <>
                        <div className="message-sources-row">
                          {msg.selectedSources && msg.selectedSources.length > 0 && (
                            <div className="used-source-chips">
                              {msg.selectedSources.map(sid => {
                                const meta = SOURCE_CHIP_META[sid];
                                return meta ? (
                                  <span key={sid} className="used-source-chip">
                                    {meta.emoji} {meta.label}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          )}
                          {/* Only show source badge when no chips selected — chips already communicate the source. Dev 2026-07-07 */}
                          {msg.source && msg.source !== 'error' &&
                           !(msg.selectedSources && msg.selectedSources.length > 0) && (
                            <span className={`message-source source-${msg.source}`}>
                              {msg.source === 'knowledge_base' && msg.citations && msg.citations.length > 0
                                ? `📚 ${msg.citations.slice(0, 2).join(', ')}${msg.citations.length > 2 ? ` +${msg.citations.length - 2} more` : ''}`
                                : (SOURCE_LABELS[msg.source] || msg.source)
                              }
                            </span>
                          )}
                          {msg.source === 'error' && (
                            <span className="message-source source-error">
                              {SOURCE_LABELS.error}
                            </span>
                          )}
                        </div>
                        {(() => {
                          // Only show feedback for sources where corrections are meaningful.
                          // Live data connectors (gsc, ga4, sheets, karma, gbp, project_team)
                          // must NOT get feedback — a thumbs-up would inject stale live data
                          // into the KB and pollute future answers.
                          const FEEDBACK_SOURCES = new Set(['web_search', 'llm_knowledge', 'knowledge_base']);
                          const src = msg.source || '';
                          const showFeedback = src !== 'error' && src !== 'guard' && FEEDBACK_SOURCES.has(src) && activeId;
                          return showFeedback ? (
                            <div className="feedback-bar-wrap">
                              <FeedbackBar
                                message={msg}
                                prevUserMsg={prevUser?.content || ''}
                                conversationId={activeId}
                                msgIndex={i}
                              />
                            </div>
                          ) : null;
                        })()}
                      </>
                    )}
                  </div>
                </div>


              </div>
            );
          })}

          {/* Source Picker */}
          {pendingQuestion && !loading && (
            <div className="message assistant">
              <div className="message-avatar"><img src={guruLogo} alt="Guru" className="avatar-logo" /></div>
              <div style={{ maxWidth: 580 }}>
                <SourcePicker
                  question={pendingQuestion}
                  onConfirm={confirmSources}
                  onCancel={cancelSourcePicker}
                />
              </div>
            </div>
          )}

          {loading && (
            <div className="guru-timeline">
              <div className="message-avatar"><img src={guruLogo} alt="Guru" className="avatar-logo" /></div>
              <div className="timeline-steps">
                {(statusSteps.length === 0 ? [{ label: 'Thinking', done: false }] : statusSteps).map((step, i) => (
                  <div key={i} className={`timeline-step${step.done ? ' done' : ' active'}`}>
                    <div className="timeline-dot" />
                    <span className="timeline-label">{step.label}{!step.done ? '…' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {(messages.length > 0 || loading || pendingQuestion) && (
          <div className="chat-input-area">
            <div className="chat-input-row">
              <textarea
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={
                  quotaExhausted
                    ? `Daily question limit reached — resets at ${quota.resets_at}`
                    : pendingQuestion ? 'Select sources above first…' : 'Ask an SEO question… (Shift+Enter for new line)'
                }
                disabled={!!pendingQuestion || loading || quotaExhausted}
              />
              <button
                className="send-btn"
                onClick={handleSend}
                disabled={loading || !input.trim() || !!pendingQuestion || quotaExhausted}
                title={quotaExhausted ? 'Daily question limit reached' : 'Send'}
              >
                ➤
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
